import { type FirebaseApp, initializeApp } from 'firebase/app';
import {
  CustomProvider,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { type Auth, getAuth } from 'firebase/auth';
import { type Functions, getFunctions } from 'firebase/functions';
import { type Firestore, getFirestore } from 'firebase/firestore';
import { type FirebaseStorage, getStorage } from 'firebase/storage';

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

interface InitializeFirebaseOptions {
  appCheckSiteKey?: string;
  appCheckDebug?: boolean | string;
}

let app: FirebaseApp | null = null;

export function initializeFirebase(
  config: FirebaseConfig,
  options: InitializeFirebaseOptions = {},
): FirebaseApp {
  if (app) {
    return app;
  }
  app = initializeApp(config);

  if (options.appCheckSiteKey) {
    if (options.appCheckDebug) {
      Reflect.set(globalThis, 'FIREBASE_APPCHECK_DEBUG_TOKEN', options.appCheckDebug);
    }
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(options.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } else if (options.appCheckDebug) {
    // Dev-only path: no reCAPTCHA site key available. Set the debug token so
    // Firebase exchanges it with the App Check backend. The token must be a
    // UUID registered in Firebase Console → App Check → debug tokens for the
    // sandbox project. Pass VITE_APPCHECK_DEBUG_TOKEN=<uuid> in .env.local.
    Reflect.set(globalThis, 'FIREBASE_APPCHECK_DEBUG_TOKEN', options.appCheckDebug);
    initializeAppCheck(app, {
      provider: new CustomProvider({
        getToken: async () => ({
          token: String(options.appCheckDebug),
          expireTimeMillis: Date.now() + 3_600_000,
        }),
      }),
      isTokenAutoRefreshEnabled: false,
    });
  }

  return app;
}

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    throw new Error('Firebase has not been initialized. Call initializeFirebase() first.');
  }
  return app;
}

export function getFirebaseAuth(): Auth {
  return getAuth(getFirebaseApp());
}

export function getFirebaseDb(): Firestore {
  return getFirestore(getFirebaseApp());
}

export function getFirebaseStorage(): FirebaseStorage {
  return getStorage(getFirebaseApp());
}

export function getFirebaseFunctions(): Functions {
  return getFunctions(getFirebaseApp());
}
